// Horizontal inset is `--slide-panel-gutter`, the same token SlidePanel uses
// for its own header — so the "Support" title, the segmented control and the
// ticket cards share one vertical grid, exactly as in the Notifications drawer.
.body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px var(--slide-panel-gutter) 24px;
}

// One clean header row: segmented control (left) + actions (right).
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

// Segmented control — blue "info" accent (own identity, distinct from
// Incident's neutral gray/white segmented toggle).
.segmented {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  background: var(--color-badge-info-bg, #dbeafe);
  border-radius: 9px;
}

.segment {
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-badge-info-fg, #1e40af);
  border-radius: 7px;
  transition:
    color 0.15s,
    background-color 0.15s,
    box-shadow 0.15s;

  &:hover {
    color: var(--color-foreground, #111827);
  }
}

.segment-active {
  background: var(--color-background, #fff);
  color: var(--color-badge-info-fg, #1e40af);
  box-shadow: 0 1px 2px rgb(0 0 0 / 10%);
}

.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

// Same card language as the Notifications drawer: shared radius token, neutral
// hairline border, the global soft `--shadow-card`, identical inner padding.
//
// Deliberately STATELESS — no hover border, no elevation change, no accent
// stripe. The blue left border this used to carry is gone: ticket status is
// already spelled out by `SupportStatusBadge`, so a coloured stripe added a
// second, weaker encoding of the same fact and made the two drawers look like
// different products.
.item {
  align-items: center;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--drawer-card-radius);
  box-shadow: var(--shadow-card);
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 13px 14px;
}

// A reporter's own ticket expands in place, so the card becomes a stack: the
// toggle keeps the original row's padding, the panel sits underneath it.
.item-expandable {
  align-items: stretch;
  flex-direction: column;
  gap: 0;
  padding: 0;
}

.item-toggle {
  align-items: center;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  gap: 12px;
  justify-content: space-between;
  padding: 13px 14px;
  text-align: left;
  width: 100%;
}

.chevron {
  color: var(--color-muted-foreground);
  flex-shrink: 0;
  height: 16px;
  transition: transform 0.15s ease;
  width: 16px;
}

.chevron-open {
  transform: rotate(180deg);
}

.item-panel {
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 14px 14px;
}

// The reporter's own words, line breaks intact.
.description {
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.edit-bar {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
}

.locked {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.item-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

// The ticket's own reference (T-140) in a drawer row's meta line. Tabular
// figures keep the digits aligned down the list.
.item-ref {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.vote {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px 8px;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 999px;
  background: transparent;
  color: var(--color-muted, #6b7280);
  font-size: 12px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.vote-active {
  color: var(--color-badge-info-fg, #1e40af);
  border-color: var(--color-badge-info-fg, #1e40af);
}

.vote-icon {
  height: 14px;
  width: 14px;
}
